import { useQuery } from "@tanstack/react-query";
import { z } from "zod";

import { NIKE_URL } from "@/data/constants/url";
import { useAuth } from "@/features/auth/shared/hooks/use-auth";
import { useLang } from "@/hooks/use-lang";
import { Lang, i18n } from "@/i18n-config";
import { Path } from "@/types/utils";
import { createHttpErrorMessage } from "@/utils/create-http-error-message";

type ChartConfigListParams = {
  symbol: string;
  lang: Lang;
  token: string;
};

export const timeFrameSchema = z.array(z.enum(["Q", "Y", "4Q", "D"]));

const chartConfigSchema = z.object({
  chartId: z.string(),
  timeframe: timeFrameSchema,
});

const chartConfigListSchema = z.object({
  data: z.object({
    overview: z.array(chartConfigSchema),
    financialReport: z.object({
      financial: z.array(chartConfigSchema),
      valuation: z.array(chartConfigSchema),
      growth: z.array(chartConfigSchema),
      profitability: z.array(chartConfigSchema),
    }),
    stockSentiment: z.array(chartConfigSchema),
  }),
  message: z.string(),
});

export type ChartConfig = z.infer<typeof chartConfigSchema>;
export type ChartConfigList = z.infer<typeof chartConfigListSchema>["data"];

export type ChartConfigListPaths = Path<ChartConfigList>;

export const getChartConfigList = async ({
  symbol,
  lang,
  token,
}: ChartConfigListParams): Promise<ChartConfigList> => {
  const endpoint = `/charts/${symbol}`;

  if (!symbol || !lang || !token) {
    throw new Error(`GET: ${endpoint}\n Missing required params`);
  }
  if (!i18n.locales.includes(lang)) {
    throw new Error(`GET: ${endpoint}\n Invalid lang: ${lang}`);
  }

  const headers = new Headers({
    "Accept-Language": lang,
    "X-User-Profile": token,
  });
  const res = await fetch(`${NIKE_URL}${endpoint}`, {
    method: "GET",
    headers,
  });
  if (!res.ok) {
    const errorMessage = await createHttpErrorMessage(endpoint, res);
    throw new Error(errorMessage);
  }
  return chartConfigListSchema.parse(await res.json()).data;
};

export const useChartConfigList = (symbol: string) => {
  const { lang } = useLang();
  const { backendToken } = useAuth();
  return useQuery(
    ["chart-config-list", symbol, lang, backendToken],
    () => getChartConfigList({ symbol, lang, token: backendToken ?? "" }),
    { enabled: !!symbol && !!backendToken }
  );
};
